import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, type ArtStationImportStatus } from "../../lib/api";

export function AdminDashboard() {
  const [stats, setStats] = useState({
    projects: 0,
    published: 0,
    drafts: 0,
    categories: 0,
    views: 0,
  });
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [asUser, setAsUser] = useState("jmartgraphix");
  const [asDryRun, setAsDryRun] = useState(false);
  const [asPublish, setAsPublish] = useState(false);
  const [importStatus, setImportStatus] = useState<ArtStationImportStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [flUser, setFlUser] = useState("samuraijkm");
  const [flDryRun, setFlDryRun] = useState(false);
  const [flPublish, setFlPublish] = useState(false);
  const [flStatus, setFlStatus] = useState<ArtStationImportStatus | null>(null);
  const flPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [vmUser, setVmUser] = useState("jmartgraphix");
  const [vmDryRun, setVmDryRun] = useState(false);
  const [vmPublish, setVmPublish] = useState(false);
  const [vmStatus, setVmStatus] = useState<ArtStationImportStatus | null>(null);
  const vmPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function refreshStats() {
    Promise.all([
      api.adminProjects({ perPage: 1 }),
      api.adminProjects({ visibility: "published", perPage: 1 }),
      api.adminProjects({ visibility: "draft", perPage: 1 }),
      api.adminCategories(),
      api.adminViews(),
    ]).then(([all, pub, draft, cats, views]) => {
      setStats({
        projects: all.meta.total,
        published: pub.meta.total,
        drafts: draft.meta.total,
        categories: cats.length,
        views: views.length,
      });
    });
  }

  useEffect(() => {
    refreshStats();
    api.importArtStationStatus().then(setImportStatus).catch(() => {});
    api.importFlickrStatus().then(setFlStatus).catch(() => {});
    api.importVimeoStatus().then(setVmStatus).catch(() => {});
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (flPollRef.current) clearInterval(flPollRef.current);
      if (vmPollRef.current) clearInterval(vmPollRef.current);
    };
  }, []);

  function startPolling() {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = await api.importArtStationStatus();
        setImportStatus(s);
        if (!s.running) {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          refreshStats();
          if (s.result) {
            setMsg(
              `ArtStation import finished: ${s.result.imported} new, ${s.result.skipped} skipped, ${s.result.errors} errors (${s.result.found} on profile)`
            );
          } else if (s.error) {
            setErr(s.error);
          }
        }
      } catch {
        /* keep polling */
      }
    }, 2000);
  }

  function startFlickrPolling() {
    if (flPollRef.current) clearInterval(flPollRef.current);
    flPollRef.current = setInterval(async () => {
      try {
        const s = await api.importFlickrStatus();
        setFlStatus(s);
        if (!s.running) {
          if (flPollRef.current) clearInterval(flPollRef.current);
          flPollRef.current = null;
          refreshStats();
          if (s.result) {
            setMsg(
              `Flickr import finished: ${s.result.imported} new, ${s.result.skipped} skipped, ${s.result.errors} errors (${s.result.found} on photostream)`
            );
          } else if (s.error) {
            setErr(s.error);
          }
        }
      } catch {
        /* keep polling */
      }
    }, 2000);
  }

  async function reindex() {
    setErr(null);
    try {
      const r = await api.reindex();
      setMsg(`Typesense reindexed ${r.indexed} projects`);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function runArtStationImport() {
    setErr(null);
    setMsg(null);
    try {
      const res = await api.importArtStation({
        username: asUser.trim() || "jmartgraphix",
        dryRun: asDryRun,
        publish: asPublish,
      });
      setImportStatus(res.status);
      setMsg(res.message);
      if (res.status.running) startPolling();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function runFlickrImport() {
    setErr(null);
    setMsg(null);
    try {
      const res = await api.importFlickr({
        username: flUser.trim() || "samuraijkm",
        dryRun: flDryRun,
        publish: flPublish,
      });
      setFlStatus(res.status);
      setMsg(res.message);
      if (res.status.running) startFlickrPolling();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  function startVimeoPolling() {
    if (vmPollRef.current) clearInterval(vmPollRef.current);
    vmPollRef.current = setInterval(async () => {
      try {
        const s = await api.importVimeoStatus();
        setVmStatus(s);
        if (!s.running) {
          if (vmPollRef.current) clearInterval(vmPollRef.current);
          vmPollRef.current = null;
          refreshStats();
          if (s.result) {
            setMsg(
              `Vimeo import finished: ${s.result.imported} new, ${s.result.skipped} skipped, ${s.result.errors} errors (${s.result.found} on profile)`
            );
          } else if (s.error) {
            setErr(s.error);
          }
        }
      } catch {
        /* keep polling */
      }
    }, 2000);
  }

  async function runVimeoImport() {
    setErr(null);
    setMsg(null);
    try {
      const res = await api.importVimeo({
        username: vmUser.trim() || "jmartgraphix",
        dryRun: vmDryRun,
        publish: vmPublish,
      });
      setVmStatus(res.status);
      setMsg(res.message);
      if (res.status.running) startVimeoPolling();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  const running = importStatus?.running;
  const logLines = importStatus?.log?.slice(-40) || [];
  const flRunning = flStatus?.running;
  const flLogLines = flStatus?.log?.slice(-40) || [];
  const vmRunning = vmStatus?.running;
  const vmLogLines = vmStatus?.log?.slice(-40) || [];

  return (
    <div className="admin-page">
      <h1>Dashboard</h1>
      <p className="admin-page__sub">Manage portfolio content protected by Authelia SSO.</p>
      {msg && <div className="admin-msg admin-msg--ok">{msg}</div>}
      {err && <div className="admin-msg admin-msg--err">{err}</div>}

      <div className="admin-cards">
        <div className="admin-stat">
          <strong>{stats.projects}</strong>
          <span>Total projects</span>
        </div>
        <div className="admin-stat">
          <strong>{stats.published}</strong>
          <span>Published</span>
        </div>
        <div className="admin-stat">
          <strong>{stats.drafts}</strong>
          <span>Drafts</span>
        </div>
        <div className="admin-stat">
          <strong>{stats.categories}</strong>
          <span>Categories</span>
        </div>
        <div className="admin-stat">
          <strong>{stats.views}</strong>
          <span>Portfolio views</span>
        </div>
      </div>

      <div className="admin-page__toolbar">
        <Link to="/admin/projects/new" className="btn btn--primary">
          New project
        </Link>
        <Link to="/admin/resume" className="btn btn--ghost">
          Edit resume
        </Link>
        <button type="button" className="btn btn--ghost" onClick={reindex}>
          Reindex search
        </button>
      </div>

      <section className="admin-import" style={{ marginTop: "2.5rem", maxWidth: 720 }}>
        <h2 style={{ fontSize: "1.1rem", margin: "0 0 0.35rem" }}>Import from ArtStation</h2>
        <p style={{ color: "var(--text-muted)", margin: "0 0 1rem", fontSize: "0.9rem" }}>
          Pull new public projects from your ArtStation profile. Existing entries (matched by
          ArtStation URL) are skipped. New work lands as drafts unless you choose publish.
        </p>

        <div className="field">
          <label htmlFor="as-user">ArtStation username</label>
          <input
            id="as-user"
            value={asUser}
            onChange={(e) => setAsUser(e.target.value)}
            disabled={!!running}
            placeholder="jmartgraphix"
          />
        </div>

        <div className="admin-check-grid" style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              checked={asDryRun}
              disabled={!!running}
              onChange={(e) => setAsDryRun(e.target.checked)}
            />
            Dry run (list only, no writes)
          </label>
          <label>
            <input
              type="checkbox"
              checked={asPublish}
              disabled={!!running || asDryRun}
              onChange={(e) => setAsPublish(e.target.checked)}
            />
            Publish new imports immediately
          </label>
        </div>

        <div className="admin-page__toolbar">
          <button
            type="button"
            className="btn btn--primary"
            disabled={!!running}
            onClick={runArtStationImport}
          >
            {running ? "Import running…" : "Import from ArtStation"}
          </button>
          {importStatus?.finishedAt && !running && (
            <span style={{ color: "var(--text-dim)", fontSize: "0.85rem" }}>
              Last run {new Date(importStatus.finishedAt).toLocaleString()}
            </span>
          )}
        </div>

        {importStatus?.result && !running && (
          <div
            className="admin-msg admin-msg--ok"
            style={{ marginTop: "1rem", whiteSpace: "pre-wrap" }}
          >
            Found {importStatus.result.found} · imported {importStatus.result.imported} · skipped{" "}
            {importStatus.result.skipped} · errors {importStatus.result.errors}
            {importStatus.result.created.length > 0 && (
              <>
                {"\n"}New:{" "}
                {importStatus.result.created.map((c) => c.title).join(", ")}
              </>
            )}
          </div>
        )}

        {logLines.length > 0 && (
          <pre
            style={{
              marginTop: "1rem",
              padding: "0.85rem 1rem",
              background: "var(--bg-elevated)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              fontSize: "0.78rem",
              lineHeight: 1.45,
              maxHeight: 280,
              overflow: "auto",
              color: "var(--text-muted)",
            }}
          >
            {logLines.join("\n")}
          </pre>
        )}
      </section>

      <section className="admin-import" style={{ marginTop: "2.5rem", maxWidth: 720 }}>
        <h2 style={{ fontSize: "1.1rem", margin: "0 0 0.35rem" }}>Import from Flickr</h2>
        <p style={{ color: "var(--text-muted)", margin: "0 0 1rem", fontSize: "0.9rem" }}>
          Pull public photos from your Flickr photostream and tag them as{" "}
          <strong>Photography</strong>. Existing entries (matched by Flickr URL) are skipped.
        </p>

        <div className="field">
          <label htmlFor="fl-user">Flickr username / path alias</label>
          <input
            id="fl-user"
            value={flUser}
            onChange={(e) => setFlUser(e.target.value)}
            disabled={!!flRunning}
            placeholder="samuraijkm"
          />
        </div>

        <div className="admin-check-grid" style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              checked={flDryRun}
              disabled={!!flRunning}
              onChange={(e) => setFlDryRun(e.target.checked)}
            />
            Dry run (list only, no writes)
          </label>
          <label>
            <input
              type="checkbox"
              checked={flPublish}
              disabled={!!flRunning || flDryRun}
              onChange={(e) => setFlPublish(e.target.checked)}
            />
            Publish new imports immediately
          </label>
        </div>

        <div className="admin-page__toolbar">
          <button
            type="button"
            className="btn btn--primary"
            disabled={!!flRunning}
            onClick={runFlickrImport}
          >
            {flRunning ? "Import running…" : "Import from Flickr"}
          </button>
          {flStatus?.finishedAt && !flRunning && (
            <span style={{ color: "var(--text-dim)", fontSize: "0.85rem" }}>
              Last run {new Date(flStatus.finishedAt).toLocaleString()}
            </span>
          )}
        </div>

        {flStatus?.result && !flRunning && (
          <div
            className="admin-msg admin-msg--ok"
            style={{ marginTop: "1rem", whiteSpace: "pre-wrap" }}
          >
            Found {flStatus.result.found} · imported {flStatus.result.imported} · skipped{" "}
            {flStatus.result.skipped} · errors {flStatus.result.errors}
            {flStatus.result.created.length > 0 && (
              <>
                {"\n"}New: {flStatus.result.created.map((c) => c.title).join(", ")}
              </>
            )}
          </div>
        )}

        {flLogLines.length > 0 && (
          <pre
            style={{
              marginTop: "1rem",
              padding: "0.85rem 1rem",
              background: "var(--bg-elevated)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              fontSize: "0.78rem",
              lineHeight: 1.45,
              maxHeight: 280,
              overflow: "auto",
              color: "var(--text-muted)",
            }}
          >
            {flLogLines.join("\n")}
          </pre>
        )}
      </section>

      <section className="admin-import" style={{ marginTop: "2.5rem", maxWidth: 720 }}>
        <h2 style={{ fontSize: "1.1rem", margin: "0 0 0.35rem" }}>Import from Vimeo</h2>
        <p style={{ color: "var(--text-muted)", margin: "0 0 1rem", fontSize: "0.9rem" }}>
          Pull public videos from your Vimeo profile. Tagged as <strong>Video</strong> (and{" "}
          <strong>3D Animation</strong> when the description suggests 3D). Embeds play in-page;
          existing entries are skipped.
        </p>

        <div className="field">
          <label htmlFor="vm-user">Vimeo username</label>
          <input
            id="vm-user"
            value={vmUser}
            onChange={(e) => setVmUser(e.target.value)}
            disabled={!!vmRunning}
            placeholder="jmartgraphix"
          />
        </div>

        <div className="admin-check-grid" style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              checked={vmDryRun}
              disabled={!!vmRunning}
              onChange={(e) => setVmDryRun(e.target.checked)}
            />
            Dry run (list only, no writes)
          </label>
          <label>
            <input
              type="checkbox"
              checked={vmPublish}
              disabled={!!vmRunning || vmDryRun}
              onChange={(e) => setVmPublish(e.target.checked)}
            />
            Publish new imports immediately
          </label>
        </div>

        <div className="admin-page__toolbar">
          <button
            type="button"
            className="btn btn--primary"
            disabled={!!vmRunning}
            onClick={runVimeoImport}
          >
            {vmRunning ? "Import running…" : "Import from Vimeo"}
          </button>
          {vmStatus?.finishedAt && !vmRunning && (
            <span style={{ color: "var(--text-dim)", fontSize: "0.85rem" }}>
              Last run {new Date(vmStatus.finishedAt).toLocaleString()}
            </span>
          )}
        </div>

        {vmStatus?.result && !vmRunning && (
          <div
            className="admin-msg admin-msg--ok"
            style={{ marginTop: "1rem", whiteSpace: "pre-wrap" }}
          >
            Found {vmStatus.result.found} · imported {vmStatus.result.imported} · skipped{" "}
            {vmStatus.result.skipped} · errors {vmStatus.result.errors}
            {vmStatus.result.created.length > 0 && (
              <>
                {"\n"}New: {vmStatus.result.created.map((c) => c.title).join(", ")}
              </>
            )}
          </div>
        )}

        {vmLogLines.length > 0 && (
          <pre
            style={{
              marginTop: "1rem",
              padding: "0.85rem 1rem",
              background: "var(--bg-elevated)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              fontSize: "0.78rem",
              lineHeight: 1.45,
              maxHeight: 280,
              overflow: "auto",
              color: "var(--text-muted)",
            }}
          >
            {vmLogLines.join("\n")}
          </pre>
        )}
      </section>

      <section style={{ marginTop: "2.5rem" }}>
        <h2 style={{ fontSize: "1rem", color: "var(--text-muted)" }}>Quick links</h2>
        <ul style={{ color: "var(--text-dim)", lineHeight: 1.9 }}>
          <li>
            <code>/portfolio</code> — full portfolio
          </li>
          <li>
            <code>/portfolio/&lt;category-slug&gt;</code> — category filter (example:{" "}
            <code>/portfolio/photography</code>)
          </li>
          <li>
            <code>/portfolio/&lt;view-slug&gt;</code> — audience views from Admin → Portfolio Views
          </li>
          <li>
            <code>/resume</code> — shareable resume URL
          </li>
        </ul>
      </section>
    </div>
  );
}
